import React from 'react';
import ReactDOM from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider as StoreProvider } from 'react-redux';
import { Provider as PerxisAutoUIProvider } from '@perxis-auto/ui-kit/lib/components/Provider';
import { PersistGate } from 'redux-persist/integration/react';

import { DEFAULT_CONSTRUCTOR_IDS_MAP } from '@/constants/constructor';
import { DEFAULT_CREDIT_PARAMS_FORMATTER } from '@/constants/credit';
import { EVENTS_CHANNEL } from '@/constants/events';
import PAGES from '@/constants/pages';
import RouterProvider from '@/containers/RouterProvider';
import getCarParameters from '@/features/cars/helpers/getCarParameters';
import getFilterInitialValue from '@/features/filters/helpers/getFilterInitialValue';
import {
  formatPreloadedState,
  rootActions,
  rootQueriesApi,
  rootSelectors,
  setupStore,
} from '@/features/store';
import { CustomEventType } from '@/interfaces/common.interfaces';
import { ConstructorEnum } from '@/interfaces/constructor.interfaces';
import {
  CurrencyEnum,
  DataTypeEnum,
  DisclaimerDisplayTypeEnum,
  EventsHandlerFunctionType,
  FiltersPositionEnum,
  IInitConfig,
  LanguageEnum,
  LocationMarkerEnum,
  MapsProviderEnum,
  MediaTypeEnum,
  RouterTypeEnum,
} from '@/interfaces/settings.interfaces';
import Pages from '@/pages';
import { IConfigService } from '@/services/ConfigService';
import { II18Service } from '@/services/I18Service';
import InjectContainer, { initServices } from '@/services/InjectContainer';
import { INavigationService } from '@/services/NavigationService';
import ServiceProvider from '@/services/ServiceProvider';
import ServiceTypes from '@/services/ServiceTypes';

import '@/assets/styles/main.scss';

export default class InstockWidget {
  private readonly initConfig: IInitConfig;

  private storeWithPersistor: ReturnType<typeof setupStore>;

  private configService: IConfigService;

  private navigationService: INavigationService;

  private i18nService: II18Service;

  private readonly eventsHandler?: EventsHandlerFunctionType;

  static Settings = {
    FiltersPosition: FiltersPositionEnum,
    ConstructorType: ConstructorEnum,
    ConstructorIds: DEFAULT_CONSTRUCTOR_IDS_MAP,
    RouterType: RouterTypeEnum,
    Pages: PAGES,
    MapsProvider: MapsProviderEnum,
    LocationMarker: LocationMarkerEnum,
    Language: LanguageEnum,
    Currency: CurrencyEnum,
    MediaType: MediaTypeEnum,
    DisclaimerDisplayType: DisclaimerDisplayTypeEnum,
    DataType: DataTypeEnum,
  };

  constructor(initConfig: IInitConfig, eventsHandler?: EventsHandlerFunctionType) {
    this.initConfig = initConfig;

    const preloadedStateWithInitialState = formatPreloadedState({
      config: this.initConfig,
    });
    initServices(preloadedStateWithInitialState);

    this.configService = InjectContainer.get<IConfigService>(ServiceTypes.Config);

    this.navigationService = InjectContainer.get<INavigationService>(
      ServiceTypes.Navigation,
    );

    this.i18nService = InjectContainer.get<II18Service>(ServiceTypes.I18N);

    const { preloadedState } = this.configService;
    this.storeWithPersistor = setupStore(preloadedState);

    this.eventsHandler = eventsHandler;

    if (this.eventsHandler) {
      document.addEventListener(
        EVENTS_CHANNEL,
        this.dispatchEvent.bind(this) as EventListener,
      );
    }
  }

  dispatchEvent(e: CustomEventType<any>) {
    if (!this.eventsHandler) {
      return;
    }
    const { type } = e.detail;
    const eventData = e.detail;

    this.eventsHandler(type, eventData);
  }

  /**
   * Возвращает текущий объект автомобиля, если он существует.
   */
  getCurrentCar = () => {
    const state = this.storeWithPersistor.store.getState();
    const pathname = this.navigationService.location.pathname.split('/');

    const carsPageIndex = pathname.findIndex(path => path === PAGES.CarId.split('/')[1]);
    const carId = pathname[carsPageIndex + 1];

    if (carsPageIndex === -1 || !carId) {
      return null;
    }

    const params = getCarParameters(state.config, carId);
    const { data } = rootQueriesApi.cars.endpoints.getCar.select(params)(state);

    if (!data) {
      return null;
    }

    return data;
  };

  /**
   * Возвращает информацию об оценке авто, полученной при использовании трейд-ин калькулятора,
   * а также для получения данных об авто, для которого был осуществлён расчёт.
   * Если пользователь не произвел расчет то, возвращается null.
   */
  getTradeInEstimate = () => {
    const state = this.storeWithPersistor.store.getState();

    const tradeInResult = rootSelectors.tradeIn.tradeInSelector(state);

    return tradeInResult;
  };

  /**
   * Возвращает данные по кредитной программе и параметрам, выбранные пользователем для текущего автомобиля.
   * Если объекта автомобиля нет или пользователем не выбрана кредитная программа, возвращается null.
   */
  getSelectedCredit = () => {
    const currentCar = this.getCurrentCar();

    if (!currentCar) {
      return null;
    }

    const state = this.storeWithPersistor.store.getState();
    const { id: carId } = currentCar;

    if (!carId) {
      return null;
    }

    const { choice, isSelected } = rootSelectors.credit.selectById(state.credit, carId);

    if (!isSelected || !choice.program) {
      return null;
    }

    return { creditParams: choice, selectedProgram: choice.program };
  };

  /**
   * Возвращает массив выбранных, пользователем выгод у текущего автомобиля.
   * Если объект автомобиля или выгоды отсутствуют, возвращается null.
   */
  getSelectedBenefits = () => {
    const currentCar = this.getCurrentCar();

    if (!currentCar) {
      return null;
    }

    const state = this.storeWithPersistor.store.getState();
    const { maxBenefit, benefits } = currentCar.data;

    // Если выгоды у данного автомобиля отсутствуют
    if (!benefits || !benefits.length) {
      return null;
    }

    const selectedBenefits = rootSelectors.benefitsChoice.selectById(
      state.benefitsChoice,
      currentCar.id,
    );

    // Если пользователь осуществлял выбор выгод, они хранятся в state.benefitsChoice
    if (selectedBenefits) {
      return selectedBenefits.benefits;
    }

    // Если выгоды, приводящие к максимальной скидке, у данного автомобиля отсутствуют
    if (!maxBenefit || !maxBenefit.length) {
      return [];
    }

    // Если пользователь не менял выбор выгод по умолчанию, то выбранные выгоды хранятся в maxBenefit.
    const preselectedBenefits = benefits.filter(benefit =>
      maxBenefit.some(maxBenefit => maxBenefit.id === benefit.id),
    );

    return preselectedBenefits;
  };

  /**
   * Устанавливает значение фильтра
   * @param key ключ фильтра
   * @param value значение
   */
  setFilter = (key: string, value: any) => {
    const state = this.storeWithPersistor.store.getState();

    const aggregateParams = rootSelectors.filters.aggregateParametersSelector(state);
    const flattenFilterGridItems =
      rootSelectors.config.flattenFilterGridItemsSelector(state);
    const filterGridItem = flattenFilterGridItems.find(item => item.key === key);
    const { data } =
      rootQueriesApi.filters.endpoints.getAggregateFilters.select(aggregateParams)(state);
    const filterValue = getFilterInitialValue(key, value, filterGridItem, data);

    if (filterValue) {
      this.storeWithPersistor.store.dispatch(
        rootActions.choice.setFilters({ [`${key}`]: filterValue }),
      );
    }
  };

  init = async () => {
    const { element, credit, tradeIn, iconSet } = this.configService.settings;

    const rootElement = document.querySelector(element);
    if (!rootElement) {
      throw new Error(`Виджет АВН. Не найден элемент ${element}`);
    }
    const root = ReactDOM.createRoot(rootElement);

    const isCreditEnable = !!credit?.enable && !!credit.apiRoot && !!credit.clientId;
    const isTradeInEnable = !!tradeIn?.enable && !!tradeIn.apiRoot && !!tradeIn.clientId;

    let app = (
      <div className="perxis-auto-instock">
        <PerxisAutoUIProvider iconSet={iconSet}>
          <I18nextProvider i18n={this.i18nService.i18n}>
            <StoreProvider store={this.storeWithPersistor.store}>
              <PersistGate loading={null} persistor={this.storeWithPersistor.persistor}>
                <ServiceProvider container={InjectContainer}>
                  <RouterProvider>
                    <Pages />
                  </RouterProvider>
                </ServiceProvider>
              </PersistGate>
            </StoreProvider>
          </I18nextProvider>
        </PerxisAutoUIProvider>
      </div>
    );

    if (isTradeInEnable) {
      try {
        const { default: TradeInInstock } = await import('@perxis-auto/trade-in-instock');

        app = (
          <TradeInInstock.Provider
            clientId={tradeIn.clientId!}
            apiRoot={tradeIn.apiRoot!}
          >
            {app}
          </TradeInInstock.Provider>
        );
      } catch {
        console.error('Произошла ошибка при импорте пакета Trade-in!');
      }
    }

    if (isCreditEnable) {
      try {
        const {
          Provider: CreditProvider,
          getPreloadedStateWithInitialState,
          setupStore,
        } = await import('@perxis-auto/credit-sdk');

        const creditParamsFormatter =
          credit.creditParamsFormatter || DEFAULT_CREDIT_PARAMS_FORMATTER;

        const { currency } = this.storeWithPersistor.store.getState().config;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const preloadedState = getPreloadedStateWithInitialState({
          config: {
            apiRoot: credit.apiRoot!,
            clientId: credit.clientId!,
            creditParamsFormatter: choice => {
              return {
                ...creditParamsFormatter(choice),
                currency,
              };
            },
          },
        });
        const store = setupStore(preloadedState);

        app = <CreditProvider store={store}>{app}</CreditProvider>;
      } catch {
        console.error('Произошла ошибка при импорте пакета кредитного калькулятора!');
      }
    }

    root.render(app);
  };
}
